export const CHAT_INSTANCE_STATUS = {
  error: 'error',
  ready: 'ready',
  streaming: 'streaming',
  submitted: 'submitted',
} as const;

export type ChatInstanceStatus =
  (typeof CHAT_INSTANCE_STATUS)[keyof typeof CHAT_INSTANCE_STATUS];

export function isActiveChatInstanceStatus(
  status: ChatInstanceStatus | null | undefined,
): status is
  | typeof CHAT_INSTANCE_STATUS.streaming
  | typeof CHAT_INSTANCE_STATUS.submitted {
  return (
    status === CHAT_INSTANCE_STATUS.streaming ||
    status === CHAT_INSTANCE_STATUS.submitted
  );
}

export type HttpRequestError = Error & {
  statusCode: number;
};

export const CHAT_LOAD_ERROR_KIND = {
  connectivity: 'connectivity',
  server: 'server',
  generic: 'generic',
} as const;

export type ChatLoadErrorKind =
  (typeof CHAT_LOAD_ERROR_KIND)[keyof typeof CHAT_LOAD_ERROR_KIND];

export function normalizeChatError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export function createHttpRequestError({
  message,
  statusCode,
}: {
  message: string;
  statusCode: number;
}): HttpRequestError {
  const error = new Error(message) as HttpRequestError;

  error.name = 'HttpRequestError';
  error.statusCode = statusCode;

  return error;
}

export function isHttpRequestError(error: Error): error is HttpRequestError {
  return typeof (error as HttpRequestError).statusCode === 'number';
}

function errorMessageIncludesMarker(
  error: Error,
  markers: readonly string[],
): boolean {
  const errorMessage = error.message.toLowerCase();

  return markers.some((marker) => errorMessage.includes(marker));
}

const BROWSER_FETCH_FAILURE_PATTERNS = [
  /^failed to fetch$/i,
  /^typeerror:\s*failed to fetch$/i,
  /networkerror when attempting to fetch resource/i,
  /networkerror when calling/i,
  /err_connection_/i,
  /\bnetwork request failed\b/i,
  /\boffline\b/i,
] as const;

const AUTHENTICATION_ERROR_MARKERS = ['invalid token', '401', '403'] as const;

const RECOVERABLE_STREAM_ERROR_MARKERS = [
  'failed to fetch',
  'fetch failed',
  'econnrefused',
  'networkerror',
  'network error',
  'err_connection',
  'terminated',
  'socket',
  'stream_pending',
  'the operation was aborted',
  'bodystreambuffer was aborted',
  'econnreset',
  'canceled',
  'cancelled',
] as const;

export function isConnectivityError(error: Error): boolean {
  const message = error.message.trim();

  return BROWSER_FETCH_FAILURE_PATTERNS.some((pattern) =>
    pattern.test(message),
  );
}

export function isServerError(error: Error): boolean {
  return isHttpRequestError(error) && error.statusCode >= 500;
}

export function resolveChatLoadErrorKind(error: Error): ChatLoadErrorKind {
  if (isConnectivityError(error)) {
    return CHAT_LOAD_ERROR_KIND.connectivity;
  }

  if (isServerError(error)) {
    return CHAT_LOAD_ERROR_KIND.server;
  }

  return CHAT_LOAD_ERROR_KIND.generic;
}

const UNREACHABLE_CHAT_SEND_MARKERS = [
  'failed to fetch',
  'fetch failed',
  'econnrefused',
  'networkerror',
  'network error',
  'err_connection',
  'network request failed',
  'error occurred while trying to proxy',
] as const;

export function isUnreachableChatSendError(error: Error | undefined): boolean {
  if (!error) {
    return false;
  }

  if (errorMessageIncludesMarker(error, AUTHENTICATION_ERROR_MARKERS)) {
    return false;
  }

  return errorMessageIncludesMarker(error, UNREACHABLE_CHAT_SEND_MARKERS);
}

export function isRecoverableStreamError(error: Error | undefined): boolean {
  if (!error) {
    return false;
  }

  if (errorMessageIncludesMarker(error, AUTHENTICATION_ERROR_MARKERS)) {
    return false;
  }

  return errorMessageIncludesMarker(error, RECOVERABLE_STREAM_ERROR_MARKERS);
}

const GENERIC_CHAT_STREAM_ERROR_MESSAGE =
  'Something went wrong while processing your request. Please try again.';

const COPILOT_ERROR_LABEL_PATTERN =
  /^(SchemaValidationError|ToolExecutionError|AuthenticationRequired|GuardrailBlocked|StreamError|UnexpectedError)(?:\s*\(Chat ID:.*\))?$/i;

export function formatChatStreamErrorMessage(error: Error): string {
  if (isConnectivityError(error)) {
    return 'Unable to reach the server. Please check your internet connection and try again.';
  }

  if (isServerError(error)) {
    return 'The server is taking longer than expected. Please try again in a moment.';
  }

  if (error.message.includes('Invalid token')) {
    return 'Your session has expired. Please login again.';
  }

  if (error.message.includes('401')) {
    return 'Invalid API key';
  }

  const trimmedMessage = error.message.trim();

  if (COPILOT_ERROR_LABEL_PATTERN.test(trimmedMessage)) {
    return trimmedMessage;
  }

  if (/Chat ID:/i.test(trimmedMessage) && trimmedMessage.length < 120) {
    return trimmedMessage;
  }

  return GENERIC_CHAT_STREAM_ERROR_MESSAGE;
}
